import api from './api';

export interface TenantDto {
  id: string;
  name: string;
  ownerUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTenantRequest {
  name: string;
}

const extractArrayFromPayload = <T>(payload: unknown, keys: string[]): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const asRecord = payload as Record<string, unknown>;

  for (const key of keys) {
    const value = asRecord[key];
    if (Array.isArray(value)) {
      return value as T[];
    }
  }

  const data = asRecord.data;
  if (Array.isArray(data)) {
    return data as T[];
  }

  if (data && typeof data === 'object') {
    const nested = data as Record<string, unknown>;
    for (const key of keys) {
      const value = nested[key];
      if (Array.isArray(value)) {
        return value as T[];
      }
    }
  }

  return [];
};

const toStringOrEmpty = (value: unknown): string => (typeof value === 'string' ? value : '');

const extractTenantFromPayload = (payload: unknown): TenantDto | null => {
  const mapTenant = (item: Record<string, unknown>): TenantDto | null => {
    const id = item.id ?? item.Id;
    const name = item.name ?? item.Name;
    if (typeof id !== 'string' || typeof name !== 'string') {
      return null;
    }

    return {
      id,
      name,
      ownerUserId: toStringOrEmpty(item.ownerUserId ?? item.OwnerUserId),
      createdAt: toStringOrEmpty(item.createdAt ?? item.CreatedAt),
      updatedAt: toStringOrEmpty(item.updatedAt ?? item.UpdatedAt),
    };
  };

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const asRecord = payload as Record<string, unknown>;
  const directTenant = mapTenant(asRecord);
  if (directTenant) return directTenant;

  const candidates = ['tenant', 'data', 'item'];
  for (const key of candidates) {
    const value = asRecord[key];
    if (value && typeof value === 'object') {
      const item = value as Record<string, unknown>;
      const mapped = mapTenant(item);
      if (mapped) return mapped;
    }
  }

  return null;
};

export const tenantService = {
  async getAllTenants(): Promise<TenantDto[]> {
    try {
      const response = await api.get('/tenants');
      const raw = extractArrayFromPayload<Record<string, unknown>>(response.data, ['tenants', 'items', 'Tenants', 'Items']);
      return raw
        .map((item) => extractTenantFromPayload(item))
        .filter((tenant): tenant is TenantDto => tenant !== null);
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw error;
      }

      // API route fallback for environments using singular controller naming.
      const fallbackResponse = await api.get('/tenant');
      const raw = extractArrayFromPayload<Record<string, unknown>>(fallbackResponse.data, ['tenants', 'items', 'Tenants', 'Items']);
      return raw
        .map((item) => extractTenantFromPayload(item))
        .filter((tenant): tenant is TenantDto => tenant !== null);
    }
  },

  async getTenant(id: string): Promise<TenantDto> {
    try {
      const response = await api.get(`/tenants/${id}`);
      const tenant = extractTenantFromPayload(response.data);
      if (!tenant) {
        throw new Error('Unexpected tenant response shape');
      }
      return tenant;
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw error;
      }

      const fallbackResponse = await api.get(`/tenant/${id}`);
      const tenant = extractTenantFromPayload(fallbackResponse.data);
      if (!tenant) {
        throw new Error('Unexpected tenant response shape');
      }
      return tenant;
    }
  },

  async switchTenant(tenantId: string): Promise<void> {
    try {
      await api.post('/tenants/switch', { tenantId });
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw error;
      }
      await api.post('/tenant/switch', { tenantId });
    }

    // Update the X-Tenant-Id header for future requests
    api.defaults.headers.common['X-Tenant-Id'] = tenantId;
    localStorage.setItem('selectedTenantId', tenantId);
  },

  async createTenant(payload: CreateTenantRequest): Promise<TenantDto> {
    try {
      const response = await api.post('/tenants', payload);
      const tenant = extractTenantFromPayload(response.data);
      if (!tenant) {
        throw new Error('Unexpected tenant response shape');
      }
      return tenant;
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw error;
      }

      const fallbackResponse = await api.post('/tenant', payload);
      const tenant = extractTenantFromPayload(fallbackResponse.data);
      if (!tenant) {
        throw new Error('Unexpected tenant response shape');
      }
      return tenant;
    }
  },

  async updateTenantName(id: string, name: string): Promise<TenantDto> {
    try {
      const response = await api.put(`/tenants/${id}`, { name });
      const tenant = extractTenantFromPayload(response.data);
      if (!tenant) {
        throw new Error('Unexpected tenant response shape');
      }
      return tenant;
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw error;
      }

      const fallbackResponse = await api.put(`/tenant/${id}`, { name });
      const tenant = extractTenantFromPayload(fallbackResponse.data);
      if (!tenant) {
        throw new Error('Unexpected tenant response shape');
      }
      return tenant;
    }
  },

  async getCurrentTenant(): Promise<TenantDto> {
    try {
      const response = await api.get('/tenants/me');
      const tenant = extractTenantFromPayload(response.data);
      if (!tenant) {
        throw new Error('Unexpected tenant response shape');
      }
      return tenant;
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw error;
      }

      const fallbackResponse = await api.get('/tenant/me');
      const tenant = extractTenantFromPayload(fallbackResponse.data);
      if (!tenant) {
        throw new Error('Unexpected tenant response shape');
      }
      return tenant;
    }
  },

  async updateCurrentTenantName(name: string): Promise<TenantDto> {
    try {
      const response = await api.put('/tenants/me/name', { name });
      const tenant = extractTenantFromPayload(response.data);
      if (!tenant) {
        throw new Error('Unexpected tenant response shape');
      }
      return tenant;
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw error;
      }

      const fallbackResponse = await api.put('/tenant/me/name', { name });
      const tenant = extractTenantFromPayload(fallbackResponse.data);
      if (!tenant) {
        throw new Error('Unexpected tenant response shape');
      }
      return tenant;
    }
  },
};

